/**
 * threat-aggregator.ts — group Secure Score recommendations by threat and
 * return per-threat coverage stats. Used by the "Threats currently mitigated"
 * row list on the Secure Score sub-page (§7.4).
 */

import type { SecureScoreControlProfile } from '@/types/secureScore'

export interface ThreatCoverage {
  threat: string
  total: number
  implemented: number
  /** Implemented / total. Zero when total is zero. */
  coverage: number
}

/**
 * Walk every rec, fan its `threats[]` into a per-threat tally, return the top
 * five worst-coverage threats. Sorting worst-first lets the UI lead with the
 * largest gaps regardless of Microsoft's per-threat labeling.
 */
export function aggregateThreats(
  recs: SecureScoreControlProfile[],
): ThreatCoverage[] {
  const map = new Map<string, { total: number; implemented: number }>()
  for (const rec of recs) {
    for (const threat of rec.threats ?? []) {
      const cur = map.get(threat) ?? { total: 0, implemented: 0 }
      cur.total += 1
      if (rec.isImplemented) cur.implemented += 1
      map.set(threat, cur)
    }
  }
  return [...map.entries()]
    .map(([threat, { total, implemented }]) => ({
      threat,
      total,
      implemented,
      coverage: total > 0 ? implemented / total : 0,
    }))
    .sort((a, b) => a.coverage - b.coverage)
    .slice(0, 5)
}

export type CoverageSeverity = 'crit' | 'high' | 'med' | 'low'

/**
 * Coverage-derived severity. Worst coverage drives the most severe stripe so
 * users see red on threats they're least protected against.
 *   < 0.40              → crit
 *   0.40 ≤ x < 0.65     → high
 *   0.65 ≤ x < 0.85     → med
 *   ≥ 0.85              → low
 */
export function severityFromCoverage(coverage: number): CoverageSeverity {
  if (coverage < 0.4) return 'crit'
  if (coverage < 0.65) return 'high'
  if (coverage < 0.85) return 'med'
  return 'low'
}
